import { Activity, useEffect, useState } from "react";
import CheckboxWithImage from "@/components/ui/checkbox-with-image.tsx";
import { Dropdown, DropdownOption } from "@/components/ui/dropdown.tsx";
import { List } from "@/components/ui/list.tsx";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import useEventEmitter from "@/hooks/use-event-emitter.ts";
import { installationsSelectAction } from "@/redux/slices/installations-slice.ts";
import { modelSetGltfAction } from "@/redux/slices/model-slice.ts";
import { smartNumberSetDifferentAction, smartNumberSetSubFamilyAction } from "@/redux/slices/smart-number-slice.ts";
import { subFamilySetAction, subFamilySetImageAction } from "@/redux/slices/sub-family-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import type { Nullable } from "@/types";
import { getPlaceholderImage } from "@/utils/image.ts";
import { useSubFamiliesConfig } from "../../../providers/sub-families-config-provider.tsx";
import type { InstallationId, SubFamily } from "../../../types";

export default function SubFamilies() {
    const { subFamilies } = useSubFamiliesConfig();

    const emitEvent = useEventEmitter("sub-family-change");

    const dispatch = useAppDispatch();
    const subFamilyId = useAppSelector((state) => state.subFamily.id);
    const installationId = useAppSelector((state) => state.installations.activeId);
    const installations = useAppSelector((state) => state.installations.options);

    const [installationFilterId, setInstallationFilterId] = useState<Nullable<InstallationId>>(installationId);

    const clickSubFamily = (subFamily: SubFamily) => {
        dispatch(subFamilySetAction({ id: subFamily.id, name: subFamily.name }));
        dispatch(modelSetGltfAction(subFamily.glb_url));
        dispatch(subFamilySetImageAction(subFamily.image));
        dispatch(smartNumberSetSubFamilyAction(subFamily.smartNumber)); // This triggers a new fetch of the configuration options
        dispatch(smartNumberSetDifferentAction(false)); // Reset the message
        dispatch(installationsSelectAction(installationFilterId)); // Store the current installation id filter as this represents the installation on the currently active subfamily

        emitEvent(subFamily);
    };

    const changeInstallation = (value: string) => {
        if (!value) {
            setInstallationFilterId(null);
            return;
        }

        const installationIds = Object.keys(installations);
        if (!installationIds.includes(value)) return;

        setInstallationFilterId(value as InstallationId);
    };

    useEffect(() => {
        setInstallationFilterId(installationId);
    }, [installationId]);

    return (
        <div className="filter filter--sub-families">
            <Dropdown
                id="installation"
                value={installationFilterId ?? ""}
                onChange={(evt) => changeInstallation(evt.target.value)}
            >
                {Object.entries(installations).map(([id, label]) => (
                    <DropdownOption key={id} value={id}>
                        {label}
                    </DropdownOption>
                ))}
            </Dropdown>

            <div className="filter--sub-families-list">
                <List fallback={<NoSubFamilies />}>
                    {subFamilies.map((subFamily) => {
                        const isChecked = subFamilyId === subFamily.id;
                        const isSubfamilyForInstallation = subFamily.installationTypes.some(
                            (installationType) => installationType.id === installationFilterId,
                        );

                        return (
                            <Activity key={subFamily.id} mode={isSubfamilyForInstallation ? "visible" : "hidden"}>
                                <CheckboxWithImage
                                    image={subFamily.image ?? getPlaceholderImage()}
                                    label={<span className="filters--sub-families__label">{subFamily.name}</span>}
                                    isChecked={isChecked}
                                    onClick={() => clickSubFamily(subFamily)}
                                />
                            </Activity>
                        );
                    })}
                </List>
            </div>
        </div>
    );
}

function NoSubFamilies() {
    const t = useTranslations();

    return <p className="filters--sub-families__no-results">{t("no_subfamilies")}</p>;
}
